import { ChangeDetectorRef, Component } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';

import { CurrentUserSelectors } from '@core/account-data';
import type { User } from '@core/account-types';
import { filterBoolean, trackByProp } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import type {
  Requisition,
  RequisitionAssignedFlow,
  RequisitionAvailableFlow,
} from '@core/requisitions-types';
import { DecisionStatus, RequisitionStatus } from '@core/requisitions-types';
import {
  BoxComponent,
  DividerComponent,
  EmptyStateComponent,
  HeadingComponent,
  RtTooltip,
  SpinnerComponent,
} from '@core/user-interface-legacy';
import { UpdateContext } from '@recruitee/requisitions-data';

import { RequisitionsToolbarComponent } from '../../../components/details/requisitions-toolbar/requisitions-toolbar.component';
import { RejectionInfoboxComponent } from '../../../components/rejection-infobox/rejection-infobox.component';
import { RequisitionsFlowSelectContainerComponent } from '../../../containers/single-requisition/requisitions-flow-select-container/requisitions-flow-select-container.component';
import { RequisitionsFlowStepsListContainerComponent } from '../../../containers/single-requisition/requisitions-flow-steps-list-container/requisitions-flow-steps-list-container.component';
import { FlowPageDataService } from '../../../data/single-requisition/flow-page-data.service';
import { ApprovalFlowFacade } from '../../../facades/single-requisition/approval-flow.facade';
import { CreateJobFacade } from '../../../facades/single-requisition/create-job.facade';
import { EditApproversFacade } from '../../../facades/single-requisition/edit-approvers.facade';
import type { RequestApprovalDisabled } from '../../../facades/single-requisition/requisitions-general.facade';
import { RequisitionsGeneralFacade } from '../../../facades/single-requisition/requisitions-general.facade';
import { RequisitionFormManagerService } from '../../../services/requisition-form-manager.service';
import { RequisitionSaveService } from '../../../services/requisition-save.service';
import { RequisitionsNotificationsService } from '../../../services/requisitions-notifications.service';
import { RequisitionsSharedActionsService } from '../../../services/requisitions-shared-actions.service';
import { SingleRequisitionsActionsService } from '../../../services/single-requisitions-actions.service';

@Component({
  selector: 'rt-requisitions-flow-page',
  templateUrl: './flow-page.component.html',
  styleUrls: ['./flow-page.component.less'],
  imports: [
    BoxComponent,
    DividerComponent,
    EmptyStateComponent,
    HeadingComponent,
    RejectionInfoboxComponent,
    RequisitionsFlowSelectContainerComponent,
    RequisitionsFlowStepsListContainerComponent,
    RtTooltip,
    SpinnerComponent,
    RequisitionsToolbarComponent,
    InfoboxComponent,
    TranslatePipe,
  ],
  providers: [EditApproversFacade, ApprovalFlowFacade, FlowPageDataService],
})
export class FlowPageComponent {
  public readonly RequisitionStatus: typeof RequisitionStatus = RequisitionStatus;
  public users: Record<number, User>;
  public approvalFlow: RequisitionAssignedFlow | null;
  public departmentName?: string;
  public requisition: Requisition;
  public availableFlows: RequisitionAvailableFlow[] = [];
  public isFlowEditable = false;
  public allApproversDeleted = false;
  public isDepartmentsMode = false;
  public loaded = false;
  public isReadOnlyMode = false;
  public canCreateJob: boolean;
  public requestApprovalDisabled: Observable<RequestApprovalDisabled>;
  public hasManageActions: boolean;
  public isCurrentUserApproverInAnyStep: boolean;
  public currentUserActiveApproverDecisionId: number | null;

  public trackById = trackByProp<{ id?: number }>({ prop: 'id' });
  private currentUserId: number;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private approvalFlowFacade: ApprovalFlowFacade,
    private data: FlowPageDataService,
    private createJobFacade: CreateJobFacade,
    private requisitionsGeneralFacade: RequisitionsGeneralFacade,
    private requisitionsSharedActionsService: RequisitionsSharedActionsService,
    private requisitionsActionsService: SingleRequisitionsActionsService,
    private requisitionFormManager: RequisitionFormManagerService,
    private requisitionsNotificationsService: RequisitionsNotificationsService,
    private requisitionSave: RequisitionSaveService,
    private actions$: Actions,
    private store: Store,
    private cdRef: ChangeDetectorRef,
  ) {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(currentUser => {
        this.currentUserId = currentUser.id;
      });
  }

  public get isCurrentUserApproverInActiveStep(): boolean {
    return (
      !!this.currentUserActiveApproverDecisionId &&
      this.requisition.status === RequisitionStatus.Pending
    );
  }

  public ngOnInit() {
    combineLatest([
      this.data.requisition$,
      this.data.approvalFlow$,
      this.data.departmentName$,
      this.data.adminsMap$,
      this.data.flowsList$,
      this.data.isDepartmentsMode$,
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([requisition, flow, departmentName, users, flows, isDepartmentsMode]) => {
        this.requisition = requisition;
        this.approvalFlow = flow;
        this.departmentName = departmentName;
        this.users = users;
        this.availableFlows = flows;
        this.isDepartmentsMode = isDepartmentsMode;
        this.isFlowEditable = this.approvalFlowFacade.isFlowEditable;
        this.canCreateJob = this.createJobFacade.canCreateJob;
        this.hasManageActions = this.requisitionsGeneralFacade.hasManageRequisitionActions;
        this.isReadOnlyMode = this.requisitionsGeneralFacade.isReadOnlyMode;

        if (flow) {
          this.handleApproverCases(flow);
        }

        this.handleFillForm();
        this.handleRequestApprovalDisabled();
        this.handleApproverState();

        if (this.loaded) {
          this.cdRef.detectChanges();
        }
      });

    this.data.isLoaded$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(isLoaded => {
      this.loaded = isLoaded;
      this.cdRef.detectChanges();
    });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public cancelRequest(): void {
    this.requisitionsSharedActionsService.cancelApprovalRequest(this.requisition.id);
  }

  public duplicate(): void {
    this.requisitionsSharedActionsService.duplicate(this.requisition.id);
  }

  public archive(): void {
    this.requisitionsSharedActionsService.archive(this.requisition);
  }

  public retrieve(): void {
    this.requisitionsSharedActionsService.retrieve(this.requisition);
  }

  public delete(): void {
    this.requisitionsSharedActionsService.delete(this.requisition, true);
  }

  public createJob(): void {
    this.requisitionsSharedActionsService.createJob(this.requisition.id);
  }

  public approveRequest(): void {
    this.requisitionsActionsService.approveRequest(
      this.requisition.id,
      this.currentUserActiveApproverDecisionId!,
      this.findCurrentApprovalStep(),
    );
  }

  public rejectRequest(): void {
    this.requisitionsActionsService.rejectRequest(
      this.requisition.id,
      this.currentUserActiveApproverDecisionId!,
      this.findCurrentApprovalStep(),
    );
  }

  public requestApproval(): void {
    this.requisitionFormManager.toggleFieldsRequired();
    this.requisitionFormManager.keepValidation(true);

    this.requisitionSave
      .save(false, UpdateContext.ApprovalRequest)
      .pipe(take(1), filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        if (this.requisitionFormManager.form.valid) {
          this.requisitionFormManager.keepValidation(false);
          this.requisitionsActionsService.requestApproval(this.requisition);
        } else {
          this.requisitionsNotificationsService.fillRequiredFields(true, this.requisition.id);
        }
      });
  }

  private handleFillForm() {
    if (!this.requisitionFormManager.form) {
      this.requisitionFormManager.initializeForm();
    } else {
      this.requisitionFormManager.updateForm();
      this.requisitionFormManager.fillForm();
    }
  }

  private handleApproverCases(flow: RequisitionAssignedFlow) {
    const approverIds = [...new Set(flow.steps.map(step => step.approverIds).flat())];
    this.allApproversDeleted = approverIds.every(id => !this.users[id]);
  }

  private handleRequestApprovalDisabled(): void {
    this.requestApprovalDisabled = this.requisitionsGeneralFacade.getRequestApprovalDisabledState(
      this.allApproversDeleted,
    );
  }

  private handleApproverState(): void {
    this.isCurrentUserApproverInAnyStep = !!this.approvalFlow?.steps.some(step =>
      step.approverIds.some(id => this.currentUserId === id),
    );
    const currentApprovalStep = this.approvalFlow?.steps.find(step => step.status === 'current');

    this.currentUserActiveApproverDecisionId =
      currentApprovalStep?.approverDecisions?.find(
        approverDecision =>
          this.currentUserId === approverDecision.approverId &&
          approverDecision.status === DecisionStatus.Requested,
      )?.id || null;
  }

  private findCurrentApprovalStep(): number {
    return this.approvalFlow?.steps.findIndex(step => step.status === 'current')! + 1;
  }
}
